import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, Kpi, CategoryChip, AsOfPicker, fmtMoney0, fmtMoney2, fmtNum, todayIso } from "./_shared";

export default function FinanceVintage() {
  const [asOf, setAsOf] = useState(todayIso());
  const [cohorts, setCohorts] = useState(null);
  const [curve, setCurve] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: v }, { data: c }] = await Promise.all([
          api.get("/finance/v2/vintage", { params: { as_of: asOf }}),
          api.get("/finance/v2/cohort-curve", { params: { as_of: asOf, months: 24 }}),
        ]);
        setCohorts(v); setCurve(c);
      } catch (e) { toast.error("Load failed: " + formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [asOf]);

  const cohortRows = useMemo(() => {
    if (!cohorts) return [];
    return Object.values(cohorts.cohorts || {}).sort((a, b) => b.funding_month.localeCompare(a.funding_month));
  }, [cohorts]);
  const maxDeployed = Math.max(1, ...cohortRows.map(r => r.deployed));

  if (loading && !cohorts) return <div className="p-10 text-[#6B7280]" data-testid="vintage-loading">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="finance-vintage">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Finance V2 · preview</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0A2540]">Vintage / Cohort Analytics</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">Cases attribute to their funding month regardless of when they settle.</div>
        </div>
        <AsOfPicker value={asOf} onChange={setAsOf} testid="vintage-as-of" />
      </div>

      <Section title="Cohorts by funding month" category="mixed">
        <div className="overflow-auto rounded-md border border-[#E5E7EB] bg-white">
          <table className="w-full text-xs" data-testid="cohort-table">
            <thead className="bg-[#F9FAFB] sticky top-0">
              <tr className="text-left text-[#6B7280] uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">Month</th>
                <th className="px-3 py-2 font-medium text-right">Cases</th>
                <th className="px-3 py-2 font-medium">Deployed</th>
                <th className="px-3 py-2 font-medium text-right">Open</th>
                <th className="px-3 py-2 font-medium text-right">Settled</th>
                <th className="px-3 py-2 font-medium text-right">Unresolved</th>
                <th className="px-3 py-2 font-medium text-right">Realized revenue</th>
                <th className="px-3 py-2 font-medium text-right">Realized loss</th>
                <th className="px-3 py-2 font-medium text-right">Avg duration</th>
              </tr>
            </thead>
            <tbody>
              {cohortRows.map(r => (
                <tr key={r.funding_month} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`cohort-${r.funding_month}`}>
                  <td className="px-3 py-2 font-mono">{r.funding_month}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.cases}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2 bg-[#EFF6FF] rounded flex-1 max-w-[140px]">
                        <div className="h-2 bg-[#1E40AF] rounded" style={{ width: `${(r.deployed / maxDeployed) * 100}%` }} />
                      </div>
                      <span className="tabular-nums text-[11px]">{fmtMoney0(r.deployed)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#0066FF]">{r.still_open_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.settled_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#92400E]">{r.unresolved_realized_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#065F46]">{fmtMoney2(r.realized_funding_revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#991B1B]">{r.realized_principal_loss > 0 ? fmtMoney2(r.realized_principal_loss) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{r.avg_case_duration_days ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Completion curve — months since funding" category="mixed"
               subtitle={curve ? `${curve.total_cases_in_scope} loans in scope, ${curve.months_window} month window` : ""}>
        <div className="overflow-auto rounded-md border border-[#E5E7EB] bg-white">
          <table className="w-full text-xs" data-testid="curve-table">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left text-[#6B7280] uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">Month</th>
                <th className="px-3 py-2 font-medium text-right">Resolved in month</th>
                <th className="px-3 py-2 font-medium text-right">Cumulative resolved %</th>
                <th className="px-3 py-2 font-medium text-right">Principal returned in month</th>
                <th className="px-3 py-2 font-medium text-right">Revenue in month</th>
                <th className="px-3 py-2 font-medium text-right">Cumulative revenue</th>
                <th className="px-3 py-2 font-medium text-right">Eligible cohort</th>
              </tr>
            </thead>
            <tbody>
              {(curve?.buckets || []).map(b => (
                <tr key={b.month_since_funding} className="border-b border-[#F3F4F6]">
                  <td className="px-3 py-2 font-mono">M{b.month_since_funding}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.cases_resolved_in_month}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(b.cumulative_resolved_pct_of_all * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney2(b.principal_returned_in_month)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney2(b.revenue_in_month)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#065F46]">{fmtMoney2(b.cumulative_revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{b.eligible_cohort_size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-[#6B7280] mt-2">
          {(curve?.notes || []).map((n, i) => <div key={i}>· {n}</div>)}
        </div>
      </Section>
    </div>
  );
}
